import {
  CalendarToday as CalendarIcon,
  CheckCircle as CheckIcon,
  DriveFolderUpload as DriveIcon,
  Email as EmailIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, createTheme, CssBaseline, List, ListItem, ListItemText, Paper, ThemeProvider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useOAuthHealth } from "../hooks/useOAuthHealth";
import { googleServicesService, type GoogleDriveFile, type GoogleEmail, type GoogleEvent, type GoogleServicesStatus } from "../services/GoogleServicesService";

// Create a clean, neutral theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#2c3e50",
      light: "#34495e",
      dark: "#1a252f",
    },
    secondary: {
      main: "#95a5a6",
      light: "#bdc3c7",
      dark: "#7f8c8d",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
    },
    divider: "#ecf0f1",
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

export const GoogleServicesPage: React.FC = () => {
  const [servicesStatus, setServicesStatus] = useState<GoogleServicesStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<GoogleEvent[]>([]);
  const [driveFiles, setDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [emails, setEmails] = useState<GoogleEmail[]>([]);

  // OAuth health check
  const { isPluginHealthy } = useOAuthHealth();
  const isGoogleHealthy = isPluginHealthy("GooglePlugin");

  // Load services status on component mount
  useEffect(() => {
    loadServicesStatus();
  }, []);

  // Load services status
  const loadServicesStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log("GoogleServicesPage: loadServicesStatus called (PAGE LOAD)");
      // Bypass debounce on initial load to get fresh status
      const status = await googleServicesService.forceCheck(true);
      console.log("GoogleServicesPage: loadServicesStatus completed (PAGE LOAD), status:", {
        calendar: status.calendar.isConnected,
        drive: status.drive.isConnected,
        email: status.email.isConnected,
        emailError: status.email.error,
      });
      setServicesStatus(status);
    } catch (error) {
      console.error("Failed to load services status:", error);
      setError("Failed to load Google services status");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle connect Google services
  const handleConnectServices = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      await googleServicesService.requestPermissions();
      // The user will be redirected to Google OAuth
    } catch (error) {
      console.error("Failed to request permissions:", error);
      setError("Failed to request Google services permissions");
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle re-authentication
  const handleReauthenticate = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      await googleServicesService.clearAndReauthenticate();
      // The user will be redirected to Google OAuth
    } catch (error) {
      console.error("Failed to re-authenticate:", error);
      setError("Failed to re-authenticate with Google services");
    } finally {
      setIsConnecting(false);
    }
  };

  // Load sample data for connected services
  const loadSampleData = async () => {
    try {
      if (servicesStatus?.calendar.isConnected) {
        const events = await googleServicesService.getCalendarEvents(5);
        setCalendarEvents(events);
      }

      if (servicesStatus?.drive.isConnected) {
        const files = await googleServicesService.getDriveFiles(5);
        setDriveFiles(files);
      }

      if (servicesStatus?.email.isConnected) {
        const emails = await googleServicesService.getEmails(5);
        setEmails(emails);
      }
    } catch (error) {
      console.error("Failed to load sample data:", error);
      setError("Failed to load sample data from Google services");
    }
  };

  // Get service icon
  const getServiceIcon = (service: "calendar" | "drive" | "email") => {
    switch (service) {
      case "calendar":
        return <CalendarIcon />;
      case "drive":
        return <DriveIcon />;
      case "email":
        return <EmailIcon />;
    }
  };

  // Get service name
  const getServiceName = (service: "calendar" | "drive" | "email") => {
    switch (service) {
      case "calendar":
        return "Google Calendar";
      case "drive":
        return "Google Drive";
      case "email":
        return "Gmail";
    }
  };

  // Format date using DateUtils for consistent handling
  const formatDate = (dateString: string) => {
    if (!dateString) return "Unknown date";
    try {
      // Google Drive API returns dates in ISO 8601 format (e.g., "2025-11-05T21:00:00.000Z")
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString();
    } catch (error) {
      console.error("Error formatting date:", error, dateString);
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h4" component="h1">
              Google Services
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={async () => {
                setIsLoading(true);
                try {
                  console.log("GoogleServicesPage: Refresh button clicked (REFRESH STATUS)");
                  // Force a fresh check by bypassing debounce
                  const status = await googleServicesService.forceCheck(true);
                  console.log("GoogleServicesPage: Refresh button completed (REFRESH STATUS), status:", {
                    calendar: status.calendar.isConnected,
                    drive: status.drive.isConnected,
                    email: status.email.isConnected,
                    emailError: status.email.error,
                  });
                  setServicesStatus(status);
                } catch (error) {
                  console.error("Failed to refresh services status:", error);
                  setError("Failed to refresh services status");
                } finally {
                  setIsLoading(false);
                }
              }}
              disabled={isLoading}
            >
              Refresh Status
            </Button>
          </Box>

          {/* OAuth Reconnection Alert */}
          {!isGoogleHealthy && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Google Services Connection Issues
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your Google services connection needs attention. Some features may not work properly until you reconnect.
              </Typography>
              <Button variant="outlined" size="small" onClick={handleReauthenticate} disabled={isConnecting}>
                {isConnecting ? "Reconnecting..." : "Reconnect Now"}
              </Button>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Overall Status */}
          {servicesStatus && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="h6">Overall Status:</Typography>
              <Chip
                label={servicesStatus.overall.isConnected ? "Connected" : "Not Connected"}
                color={servicesStatus.overall.isConnected ? "success" : "warning"}
                icon={servicesStatus.overall.isConnected ? <CheckIcon /> : <ErrorIcon />}
              />
              {servicesStatus.overall.connectedServices.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Connected: {servicesStatus.overall.connectedServices.join(", ")}
                </Typography>
              )}
            </Box>
          )}

          {/* Connect/Re-authenticate Buttons */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {servicesStatus && !servicesStatus.overall.isConnected && (
              <Button variant="contained" size="large" startIcon={<LinkIcon />} onClick={handleConnectServices} disabled={isConnecting}>
                {isConnecting ? "Connecting..." : "Connect Google Services"}
              </Button>
            )}

            {servicesStatus && servicesStatus.overall.isConnected && (
              <>
                <Button variant="outlined" size="large" onClick={loadSampleData}>
                  Load Sample Data
                </Button>
                <Button variant="outlined" color="warning" size="large" onClick={handleReauthenticate} disabled={isConnecting}>
                  {isConnecting ? "Re-authenticating..." : "Re-authenticate"}
                </Button>
              </>
            )}
          </Box>
        </Paper>

        {/* Services Grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3 }}>
          {/* Calendar Service */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">{getServiceName("calendar")}</Typography>
              </Box>

              {servicesStatus && (
                <Box sx={{ mb: 2 }}>
                  <Chip label={servicesStatus.calendar.isConnected ? "Connected" : "Not Connected"} color={servicesStatus.calendar.isConnected ? "success" : "warning"} size="small" sx={{ mb: 1 }} />
                  {servicesStatus.calendar.error && (
                    <Typography variant="caption" color="error" display="block">
                      {servicesStatus.calendar.error}
                    </Typography>
                  )}
                </Box>
              )}

              {calendarEvents.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent Events:
                  </Typography>
                  <List dense>
                    {calendarEvents.slice(0, 3).map((event) => (
                      <ListItem key={event.id} sx={{ px: 0 }}>
                        <ListItemText primary={event.summary} secondary={formatDate(event.start)} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Drive Service */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <DriveIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">{getServiceName("drive")}</Typography>
              </Box>

              {servicesStatus && (
                <Box sx={{ mb: 2 }}>
                  <Chip label={servicesStatus.drive.isConnected ? "Connected" : "Not Connected"} color={servicesStatus.drive.isConnected ? "success" : "warning"} size="small" sx={{ mb: 1 }} />
                  {servicesStatus.drive.error && (
                    <Typography variant="caption" color="error" display="block">
                      {servicesStatus.drive.error}
                    </Typography>
                  )}
                </Box>
              )}

              {driveFiles.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent Files:
                  </Typography>
                  <List dense>
                    {driveFiles.slice(0, 3).map((file) => (
                      <ListItem key={file.id} sx={{ px: 0 }}>
                        <ListItemText primary={file.name} secondary={formatDate(file.modifiedTime)} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Email Service */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">{getServiceName("email")}</Typography>
              </Box>

              {servicesStatus && (
                <Box sx={{ mb: 2 }}>
                  <Chip label={servicesStatus.email.isConnected ? "Connected" : "Not Connected"} color={servicesStatus.email.isConnected ? "success" : "warning"} size="small" sx={{ mb: 1 }} />
                  {servicesStatus.email.error && (
                    <Typography variant="caption" color="error" display="block">
                      {servicesStatus.email.error}
                    </Typography>
                  )}
                </Box>
              )}

              {emails.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent Emails:
                  </Typography>
                  <List dense>
                    {emails.slice(0, 3).map((email) => (
                      <ListItem key={email.id} sx={{ px: 0 }}>
                        <ListItemText primary={email.subject} secondary={`${email.from} - ${email.snippet.substring(0, 50)}...`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
